/**
 * Admin page of the wallets that signed in to Morcast. It lists every wallet, the newest first,
 * with its first and latest sign-in and what it has done so far, and marks the wallets that are
 * new since this admin last opened the page. The admin navigation counts the same new wallets
 * (`admin/queues.ts`), so the team notices a first sign-in from any admin page.
 */

import { Wallet } from "lucide-react";
import { useEffect, useState } from "react";

import {
  type AdminWallet,
  isNewWallet,
  markWalletsSeen,
  useAdminWallets,
  useWalletsSeenUntil,
  WALLETS_LISTED,
  WALLETS_LISTED_AT_MOST,
  walletRoles,
} from "../../admin/wallets.js";
import { useSession } from "../../auth/session.js";
import { AddressLink } from "../../components/ChainLinks.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Fact,
  Loading,
  PageHeader,
  Stat,
  styles,
} from "../../components/ui.js";
import { useAppConfig } from "../../config.js";
import { formatUtc } from "../../format.js";

export function AdminWallets() {
  return (
    <RequireAdmin>
      <WalletsPage />
    </RequireAdmin>
  );
}

function WalletsPage() {
  const admin = useSession().data?.wallet;
  const [limit, setLimit] = useState(WALLETS_LISTED);
  const list = useAdminWallets(limit);
  const stored = useWalletsSeenUntil(admin);
  // What the admin had seen when the page opened. The wallets that were new then keep their mark
  // for this visit, although opening the page marks them seen for the navigation.
  const [seenAtOpen, setSeenAtOpen] = useState<{ until: string | null } | null>(null);
  const newest = list.data?.wallets[0]?.firstSignInAt;

  useEffect(() => {
    if (!admin || newest === undefined) {
      return;
    }
    setSeenAtOpen((seen) => seen ?? { until: stored });
    // The list is the newest first, so its first wallet is the newest the admin has now seen.
    if (stored === null || Date.parse(newest) > Date.parse(stored)) {
      markWalletsSeen(admin, newest);
    }
  }, [admin, newest, stored]);

  const wallets = list.data?.wallets ?? [];
  const total = list.data?.total ?? 0;
  // Until the first list has loaded, nothing is known to be new.
  const fresh = seenAtOpen ? wallets.filter((wallet) => isNewWallet(wallet, seenAtOpen.until)) : [];
  const dayAgo = Date.now() - 86_400_000;

  return (
    <section>
      <PageHeader eyebrow="Admin" title="Wallets" documentTitle="Wallets · Admin">
        Every wallet that signed in to Morcast, the newest first. A wallet is marked new until you
        have opened this page, and the admin navigation counts the new ones.
      </PageHeader>

      {list.isPending && (
        <div className="mt-10">
          <Loading />
        </div>
      )}
      {list.isError && (
        <div className="mt-10">
          <ErrorMessage error={list.error} />
        </div>
      )}
      {list.data && wallets.length === 0 && (
        <div className="mt-10">
          <EmptyState icon={Wallet} title="No wallet has signed in yet">
            A wallet appears here the first time it signs in.
          </EmptyState>
        </div>
      )}
      {list.data && wallets.length > 0 && (
        <>
          <dl className="mt-8 grid gap-3 sm:grid-cols-3">
            <Stat label="Wallets" value={total.toLocaleString("en-US")} hint="Signed in ever" />
            <Stat
              label="New"
              value={fresh.length.toLocaleString("en-US")}
              hint="Since you last opened this page"
            />
            <Stat
              label="Active"
              value={wallets
                .filter((wallet) => Date.parse(wallet.lastSignInAt) > dayAgo)
                .length.toLocaleString("en-US")}
              hint="Signed in during the last 24 hours"
            />
          </dl>

          <ul aria-label="Wallets" className="mt-6 space-y-3">
            {wallets.map((wallet) => (
              <WalletRow
                key={wallet.address}
                wallet={wallet}
                isNew={seenAtOpen !== null && isNewWallet(wallet, seenAtOpen.until)}
              />
            ))}
          </ul>

          {total > wallets.length && (
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-zinc-600 dark:text-zinc-400">
              <span>
                Showing the newest {wallets.length.toLocaleString("en-US")} of{" "}
                {total.toLocaleString("en-US")} wallets.
              </span>
              {limit < WALLETS_LISTED_AT_MOST && (
                <button
                  type="button"
                  onClick={() => setLimit(WALLETS_LISTED_AT_MOST)}
                  className={styles.secondaryButton}
                >
                  Show more
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** One wallet: its address, what it is, what it has done and when it signed in. */
function WalletRow({ wallet, isNew }: { wallet: AdminWallet; isNew: boolean }) {
  const { chainId } = useAppConfig();
  const roles = walletRoles(wallet);
  return (
    <li
      className={`${styles.card} ${
        isNew ? "ring-brand-500/40 dark:ring-brand-400/30" : ""
      } grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] lg:items-start`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {isNew && <Badge tone="blue">New</Badge>}
          {/* Roles are plain labels. Colour is kept for what is new. */}
          {roles.map((role) => (
            <Badge key={role} tone="grey">
              {role}
            </Badge>
          ))}
          {roles.length === 0 && <Badge tone="grey">No activity yet</Badge>}
        </div>
        <p className="mt-2">
          <AddressLink chainId={chainId} address={wallet.address} />
        </p>
      </div>
      <dl className="contents text-sm">
        <Fact label="Activity">{activity(wallet)}</Fact>
        <Fact label="First sign-in">{formatUtc(wallet.firstSignInAt)}</Fact>
        <Fact label="Latest sign-in">{formatUtc(wallet.lastSignInAt)}</Fact>
      </dl>
    </li>
  );
}

/** What the wallet has done, in words, such as "2 requests" or "1 account, 3 items". */
function activity(wallet: AdminWallet): string {
  const counted = (count: number, one: string, many: string) =>
    count > 0 ? `${count.toLocaleString("en-US")} ${count === 1 ? one : many}` : null;
  const parts = [
    counted(wallet.requests, "request", "requests"),
    counted(wallet.accounts, "linked account", "linked accounts"),
    counted(wallet.submissions, "item", "items"),
  ].filter((part) => part !== null);
  return parts.length > 0 ? parts.join(", ") : "Signed in only";
}
